// Instagram configuration
export const INSTAGRAM_APP_ID = process.env.NEXT_PUBLIC_INSTAGRAM_APP_ID;
export const INSTAGRAM_APP_SECRET = process.env.INSTAGRAM_APP_SECRET;

// Facebook configuration
export const FACEBOOK_APP_ID = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID;
export const FACEBOOK_APP_SECRET = process.env.FACEBOOK_APP_SECRET;

// Threads configuration
export const THREADS_APP_ID = process.env.NEXT_PUBLIC_THREADS_APP_ID;
export const THREADS_APP_SECRET = process.env.THREADS_APP_SECRET;
export const THREADS_SCOPES = process.env.NEXT_PUBLIC_THREADS_SCOPES || 'threads_basic,threads_keyword_search';

// TikTok configuration
export const TIKTOK_CLIENT_KEY = process.env.NEXT_PUBLIC_TIKTOK_CLIENT_KEY || process.env.TIKTOK_CLIENT_KEY;
export const TIKTOK_CLIENT_SECRET = process.env.TIKTOK_CLIENT_SECRET;
// Hardcoded scopes per request (comma-separated, no spaces)
export const TIKTOK_SCOPES = 'user.info.basic,user.info.profile,user.info.stats,video.list';

// YouTube configuration
export const YOUTUBE_CLIENT_ID = process.env.NEXT_PUBLIC_YOUTUBE_CLIENT_ID || process.env.YOUTUBE_CLIENT_ID;
export const YOUTUBE_CLIENT_SECRET = process.env.YOUTUBE_CLIENT_SECRET;
export const YOUTUBE_SCOPES = 'https://www.googleapis.com/auth/youtube.readonly https://www.googleapis.com/auth/yt-analytics.readonly';

// TikTok Business configuration (for Marketing / Accounts API)
export const TIKTOK_BUSINESS_APP_ID = process.env.NEXT_PUBLIC_TIKTOK_BUSINESS_APP_ID || process.env.TIKTOK_BUSINESS_APP_ID;
export const TIKTOK_BUSINESS_SECRET = process.env.TIKTOK_BUSINESS_SECRET;
// Hardcoded scopes for TikTok Business auth (comma-separated, no spaces)
export const TIKTOK_BUSINESS_SCOPES = 'user.info.basic,advertiser.account.read';

// API versions
export const FACEBOOK_API_VERSION = process.env.NEXT_PUBLIC_FACEBOOK_API_VERSION || process.env.FACEBOOK_API_VERSION || 'v21.0';
export const INSTAGRAM_API_VERSION = process.env.NEXT_PUBLIC_INSTAGRAM_API_VERSION || process.env.INSTAGRAM_API_VERSION || 'v21.0';

// Base URL and redirects
export const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL || 'https://www.lema-app.com';
export const INSTAGRAM_REDIRECT_URI = process.env.INSTAGRAM_REDIRECT_URI || (BASE_URL + "/api/instagram/callback");
export const FACEBOOK_REDIRECT_URI = process.env.FACEBOOK_REDIRECT_URI || (BASE_URL + "/api/facebook/callback");
export const THREADS_REDIRECT_URI = process.env.THREADS_REDIRECT_URI || (BASE_URL + "/api/threads/callback");
export const TIKTOK_REDIRECT_URI = process.env.TIKTOK_REDIRECT_URI || (BASE_URL + "/api/tiktok/callback");
export const TIKTOK_BUSINESS_REDIRECT_URI = process.env.TIKTOK_BUSINESS_REDIRECT_URI || (BASE_URL + "/api/tiktok-business/callback");
export const YOUTUBE_REDIRECT_URI = process.env.YOUTUBE_REDIRECT_URI || (BASE_URL + "/api/youtube/callback");


// Supabase configuration
export const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL;
export const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
// Server-side only: service role key for privileged Supabase REST access (never expose client-side)
export const SUPABASE_SERVICE_ROLE_KEY = process.env.SUPABASE_SERVICE_ROLE_KEY;
export const SOCIAL_TOKEN_SECRET_KEY = process.env.SOCIAL_TOKEN_SECRET_KEY;

// Dify API configuration
export const DIFY_API_URL = process.env.DIFY_API_URL;
// DIFY_API_KEY_BRAND_SEARCH must be set in environment variables
export const DIFY_API_KEY_BRAND_SEARCH = process.env.DIFY_API_KEY_BRAND_SEARCH;
// Cloudflare Access credentials for Dify
export const CF_ACCESS_CLIENT_ID = process.env.CF_ACCESS_CLIENT_ID;
export const CF_ACCESS_CLIENT_SECRET = process.env.CF_ACCESS_CLIENT_SECRET;


/**
 * Retrieves the Dify API key for a user from environment variables.
 * This function should ONLY be used server-side where the userId is available.
 * - For the specific userId '16246ab3-d0fb-4f23-a361-78c033948ce3', it uses the key from DIFY_API_KEY_MOCK_USER_ID.
 * - For all other userIds, it uses the shared key from DIFY_API_KEY_CHAT.
 *
 * Example .env entries:
 * DIFY_API_KEY_MOCK_USER_ID=app-mock-key-xxxxxxx
 * DIFY_API_KEY_CHAT=app-shared-key-yyyyyyy
 *
 * @param userId - The ID of the user (retrieved from session, Supabase, etc.).
 * @returns The appropriate Dify API key, or undefined if not found.
 */
export const getDifyApiKeyForUser = (userId: string): string | undefined => {
  if (!userId) {
    console.error("getDifyApiKeyForUser called without a userId.");
    return undefined;
  }

  let keyName: string;

  if (userId === "16246ab3-d0fb-4f23-a361-78c033948ce3") {
    // Use the specific key for the mock user
    const transformedMockUserId = userId.toUpperCase().replace(/-/g, '_'); // MOCK_USER_ID
    keyName = `DIFY_API_KEY_${transformedMockUserId}`;
  } else {
    // Use the shared chat key for all other users
    keyName = 'DIFY_API_KEY_CHAT';
  }

  // Primary lookup
  let apiKey = process.env[keyName];

  // Backward-compat: if shared chat key isn't set, fall back to legacy DIFY_API_KEY
  if (!apiKey && keyName === 'DIFY_API_KEY_CHAT') {
    apiKey = process.env.DIFY_API_KEY;
    if (apiKey) {
      console.warn('Using legacy env var DIFY_API_KEY as fallback for DIFY_API_KEY_CHAT. Consider renaming to DIFY_API_KEY_CHAT.');
    }
  }

  if (!apiKey) {
    // Log a warning if the key isn't found
    console.warn(`Dify API key not found in environment variables for user ${userId} (checked env var: ${keyName}${keyName === 'DIFY_API_KEY_CHAT' ? ', fallback: DIFY_API_KEY' : ''})`);
  }

  return apiKey;
};

/**
 * Retrieves the Dify API key for the Chat Agent experience.
 * Mirrors getDifyApiKeyForUser but uses DIFY_API_KEY_CHAT_AGENT for non-mock users.
 *
 * @param userId - The ID of the user (retrieved from session, Supabase, etc.).
 * @returns The appropriate Dify API key for the Chat Agent, or undefined if not found.
 */
export const getDifyApiKeyForUserAgent = (userId: string): string | undefined => {
  if (!userId) {
    console.error("getDifyApiKeyForUserAgent called without a userId.");
    return undefined;
  }

  let keyName: string;

  if (userId === DEV_USER_ID) {
    // Use the specific key for the mock user (same behavior as chat)
    const transformedMockUserId = userId.toUpperCase().replace(/-/g, '_'); // MOCK_USER_ID
    keyName = `DIFY_API_KEY_${transformedMockUserId}`;
  } else {
    // Use the shared chat agent key for all other users
    keyName = 'DIFY_API_KEY_CHAT_AGENT';
  }

  const apiKey = process.env[keyName];

  if (!apiKey) {
    console.warn(`Dify Agent API key not found in environment variables for user ${userId} (checked env var: ${keyName})`);
  }

  return apiKey;
};

// Environment configuration
export const IS_DEV = process.env.NODE_ENV === "development";
export const USE_MOCK_AUTH = IS_DEV && process.env.NEXT_PUBLIC_USE_MOCK_AUTH === "true";
// Mock chat responses in development (useful for UI testing without API calls)
export const USE_MOCK_CHAT_RESPONSE = IS_DEV && process.env.NEXT_PUBLIC_USE_MOCK_CHAT_RESPONSE === "true";
// Enable rate limit testing in mock service (for testing rate limit dialog)
export const MOCK_RATE_LIMIT = process.env.NEXT_PUBLIC_MOCK_RATE_LIMIT === "true";

// Timeouts
export const PUBLIC_BRAND_SEARCH_TIMEOUT_MS = (() => {
  const raw = process.env.PUBLIC_BRAND_SEARCH_TIMEOUT_MS;
  if (raw && !Number.isNaN(Number(raw))) return Number(raw);
  return IS_DEV ? 1200000 : 120000; // dev: 30s, prod: 15s
})();

// Dev defaults (env-driven) for local testing without full auth wiring
// Server-only - client should send user?.id ?? null and let server resolve dev user ID
export const DEV_USER_ID = process.env.DEV_USER_ID;
export const DEV_DEFAULT_CONNECTED_SOCIAL_ACCOUNT_ID = process.env.DEV_DEFAULT_CONNECTED_SOCIAL_ACCOUNT_ID;

// Local development backend base (used by Next.js API routes when IS_DEV)
// Default to Docker network service name; override via env if needed
export const LEMA_API_DEV_BASE_URL = process.env.LEMA_API_DEV_BASE_URL || 'http://lema-api-prod:8001';

// Internal API configuration
// Ensure the URL has a protocol (prepend https:// if missing)
const rawInternalUrl = process.env.LEMA_API_INTERNAL_URL || (IS_DEV ? '' : 'https://lema-api-internal.lema-app.com');
export const LEMA_API_INTERNAL_URL = rawInternalUrl && !rawInternalUrl.startsWith('http://') && !rawInternalUrl.startsWith('https://')
  ? `https://${rawInternalUrl}`
  : rawInternalUrl;

// Internal API authentication secret (for service-to-service auth)
export const LEMA_API_INTERNAL_SECRET = process.env.LEMA_API_INTERNAL_SECRET;

// Instagram account selection configuration
export const MAX_INSTAGRAM_ACCOUNTS_VIA_FACEBOOK = parseInt(process.env.NEXT_PUBLIC_MAX_INSTAGRAM_ACCOUNTS_VIA_FACEBOOK || '1', 10);

// Social connection type constants
export const CONNECTION_TYPES = {
  INSTAGRAM_DIRECT: 'instagram_direct',
  INSTAGRAM_VIA_FACEBOOK: 'instagram_via_facebook',
  THREADS: 'threads',
  THREADS_VIA_INSTAGRAM: 'threads_via_instagram',
  TIKTOK: 'tiktok',
  TIKTOK_BUSINESS: 'tiktok_business',
  YOUTUBE: 'youtube'
} as const;

export type ConnectionType = typeof CONNECTION_TYPES[keyof typeof CONNECTION_TYPES];

// Portfolio endpoints via internal API
export const LEMA_API_INTERNAL_PORTFOLIO_GENERATE_URL = LEMA_API_INTERNAL_URL + '/api/v1/portfolio/generate';
export const LEMA_API_INTERNAL_PORTFOLIO_SNAPSHOT_URL = LEMA_API_INTERNAL_URL + '/api/v1/portfolio/snapshot/{username}';
export const LEMA_API_INTERNAL_PORTFOLIO_PRIMARY_USERNAME_URL = LEMA_API_INTERNAL_URL + '/api/v1/portfolio/primary-username';
export const LEMA_API_INTERNAL_PORTFOLIO_VIEWS_URL = LEMA_API_INTERNAL_URL + '/api/v1/portfolio/views/{username}';

// Index endpoints via internal API
export const LEMA_API_INTERNAL_INDEX_URL = LEMA_API_INTERNAL_URL + '/api/v1/instagram/accounts/index-noparent';
export const LEMA_API_INTERNAL_INDEX_STATUS_URL = LEMA_API_INTERNAL_URL + '/api/v1/instagram/accounts/index-status';

// ML service internal URL (for analytics/training endpoints)
const rawMLInternalUrl = process.env.LEMA_ML_INTERNAL_URL || (IS_DEV ? '' : 'https://lema-ml-internal.lema-app.com');
export const LEMA_ML_INTERNAL_URL = rawMLInternalUrl && !rawMLInternalUrl.startsWith('http://') && !rawMLInternalUrl.startsWith('https://')
  ? `https://${rawMLInternalUrl}`
  : rawMLInternalUrl;
export const LEMA_ML_DEV_BASE_URL = process.env.LEMA_ML_DEV_BASE_URL || 'http://lema-ml-dev:8000';

// Turnstile configuration (separate keys per surface)
export const TURNSTILE_PUBLIC_SEARCH_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY_PUBLIC_SEARCH;
export const TURNSTILE_PUBLIC_SEARCH_SECRET_KEY = process.env.TURNSTILE_SECRET_KEY_PUBLIC_SEARCH;
export const FEATURE_TURNSTILE_PUBLIC_SEARCH = (process.env.FEATURE_TURNSTILE_PUBLIC_SEARCH || 'false') === 'true';

export const TURNSTILE_SIGNUP_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY_SIGNUP;
export const TURNSTILE_SIGNUP_SECRET_KEY = process.env.TURNSTILE_SECRET_KEY_SIGNUP;
export const FEATURE_TURNSTILE_SIGNUP = (process.env.FEATURE_TURNSTILE_SIGNUP || 'false') === 'true';

export const TURNSTILE_SIGNIN_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY_SIGNIN;
export const TURNSTILE_SIGNIN_SECRET_KEY = process.env.TURNSTILE_SECRET_KEY_SIGNIN;
export const FEATURE_TURNSTILE_SIGNIN = (process.env.FEATURE_TURNSTILE_SIGNIN || 'false') === 'true';

// Geolocation API configuration
export const GEOLOCATION_FALLBACK_API_URL = 'https://ipapi.co/json/';

// Timeout constants for API requests
export const COBALT_API_TIMEOUT_MS = 10000; // 10 seconds for Cobalt API
export const DIFY_REQUEST_TIMEOUT_MS = 295000; // 295 seconds for Dify API (5s buffer from 300s Vercel Hobby limit)
